<template>
  <UIDropdownListItem
    ref="row"
    class="ChatInputOptionsSubmenu"
    :item="rowItem"
    :data-open="isOpen"
    @mousedown.prevent
    @click.stop="open()"
    @keydown="onRowKeydown"
  >
    <span class="ChatInputOptionsSubmenu__row">
      <UIIcon v-if="icon" class="ChatInputOptionsSubmenu__icon" :name="icon" />
      {{ label }}
      <UIIcon
        class="ChatInputOptionsSubmenu__chevron"
        name="angleRightRegular"
      />
    </span>

    <UIDropdown
      ref="menu"
      class="ChatInputOptionsSubmenu__menu"
      :items="items"
      :placement="menuPlacement"
      :closeOthers="false"
      @open="onMenuOpen"
      @close="onMenuClose"
      @clickedOption="emit('clickedOption', $event)"
    >
      <!-- An empty slot would fall back to ui-lib's select toggle. -->
      <template #toggle-container>
        <span class="ChatInputOptionsSubmenu__anchor" />
      </template>

      <template v-if="$slots.option" #option="{ option }">
        <slot name="option" :option="option" />
      </template>

      <template
        v-if="$slots['option-container']"
        #option-container="{ option }"
      >
        <slot name="option-container" :option="option" />
      </template>

      <template v-if="$slots['after-list']" #after-list>
        <slot name="after-list" />
      </template>
    </UIDropdown>
  </UIDropdownListItem>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, useTemplateRef } from 'vue'

import {
  UIDropdown,
  UIDropdownListItem,
  UIIcon,
  type IIcons,
} from '@foundever/ui-lib'
import { useMediaQuery } from '@vueuse/core'

/** A row in a chat input menu; matches ui-lib's `IDropdownItem`. */
export interface IChatInputOption {
  label: string
  value: string
  icon?: IIcons
}

export interface Props {
  /** Text of the row that opens the submenu. */
  label: string
  /** Leading icon of that row, if any. */
  icon?: IIcons
  /** The submenu's rows. */
  items: IChatInputOption[]
}

const props = defineProps<Props>()

export interface Emits {
  (_e: 'open'): void
  (_e: 'close'): void
  (_e: 'clickedOption', _option: IChatInputOption): void
}

const emit = defineEmits<Emits>()

defineSlots<{
  /** Content of a default submenu row, which picks and closes. */
  option?(props: { option: IChatInputOption }): unknown
  /** Replaces a whole submenu row, including ui-lib's pick-and-close. */
  'option-container'?(props: { option: IChatInputOption }): unknown
  /** Rows after the list. */
  'after-list'?(): unknown
}>()

// #region Row
const row = useTemplateRef<InstanceType<typeof UIDropdownListItem>>('row')
const rowItem = computed(() => ({ label: props.label, value: props.label }))

const rowElement = (): HTMLElement | undefined => row.value?.$el

const focusRow = () => {
  rowElement()?.focus()
}

const onRowKeydown = (event: KeyboardEvent) => {
  if (!['Enter', ' ', 'ArrowRight'].includes(event.key)) return

  // Keeps the menu's list from reading Enter as a pick.
  event.preventDefault()
  event.stopPropagation()
  open(true)
}
// #endregion

// #region Submenu
const menu = useTemplateRef<InstanceType<typeof UIDropdown>>('menu')
const isOpen = ref(false)
/** The submenu was opened from the keyboard, so closing it returns focus to the row. */
let isOpenedFromKeyboard = false

// Too narrow for the menu and the submenu side by side.
const isNarrowViewport = useMediaQuery('(max-width: 30rem)')
const menuPlacement = computed(() =>
  isNarrowViewport.value ? 'bottom-start' : 'right-start',
)

// ui-lib's UIDropdown has no open() / close(); its anchor toggles on click.
const toggleMenu = () => {
  menu.value?.$refs.anchorElement?.click()
}

const open = async (shouldFocusFirstOption = false) => {
  if (!isOpen.value) toggleMenu()
  if (!shouldFocusFirstOption) return

  isOpenedFromKeyboard = true
  await nextTick()
  menu.value?.$refs.floating
    ?.querySelector<HTMLElement>('[role="option"]')
    ?.focus()
}

const close = () => {
  if (isOpen.value) toggleMenu()
}

const onMenuOpen = () => {
  isOpen.value = true
  emit('open')
}

const onMenuClose = async () => {
  isOpen.value = false
  emit('close')
  if (!isOpenedFromKeyboard) return
  isOpenedFromKeyboard = false

  // Escape drops focus along with the submenu; hand it back to the row.
  await nextTick()
  const element = rowElement()
  if (element?.isConnected && document.activeElement === document.body) {
    element.focus()
  }
}
// #endregion

defineExpose({ open, close, focus: focusRow })
</script>

<style scoped lang="scss">
.ChatInputOptionsSubmenu {
  // Clips the overhanging submenu anchor, so the list does not scroll.
  overflow: clip;

  &[data-open='true'] {
    --bg-color: var(--interactive-bg-tertiary-hover);
  }

  .ChatInputOptionsSubmenu__row {
    display: flex;
    flex: 1;
    align-items: center;
    gap: var(---fds-global-space-xs);
    min-width: 0;
  }

  .ChatInputOptionsSubmenu__icon {
    --size: 1rem;
    --color: var(--icon-secondary);
    flex-shrink: 0;
  }

  .ChatInputOptionsSubmenu__chevron {
    --size: 0.875rem;
    --color: var(--icon-secondary);
    flex-shrink: 0;
    margin-left: auto;
  }

  // Invisible submenu anchor: the row grown by the menu's border + padding, plus a gap.
  .ChatInputOptionsSubmenu__menu {
    --row-inset: calc(1px + var(---fds-global-space-xs));
    --submenu-gap: var(---fds-global-space-xxs);

    position: absolute;
    inset: calc(-1 * var(--row-inset))
      calc(-1 * (var(--row-inset) + var(--submenu-gap)));
    // Grid stretches ui-lib's anchor to the whole box.
    display: grid;
    pointer-events: none;
  }
}
</style>
